import React, { useState, useEffect } from 'react'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress
} from '@mui/material'
import {
  People as PeopleIcon,
  Cloud as CloudIcon,
  BugReport as BugReportIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Shield as SecurityIcon
} from '@mui/icons-material'
import { systemAPI, usersAPI, cloudinaryAPI, debugAPI } from '../services/api'

function Dashboard() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, admins: 0 },
    cloudinary: { usage: 0, limit: 0, images: 0 },
    system: { status: 'unknown', uptime: 0, memory: 0 },
    debug: { errors: 0, warnings: 0, performance: 0 }
  })

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError('')

      // Load all dashboard data in parallel
      const [usersData, cloudinaryData, systemData, debugData] = await Promise.allSettled([
        usersAPI.getAll(),
        cloudinaryAPI.getStats(),
        systemAPI.getHealth(),
        debugAPI.getSystemStatus()
      ])

      // Process users data
      if (usersData.status === 'fulfilled' && usersData.value.success) {
        const users = usersData.value.data
        setStats(prev => ({
          ...prev,
          users: {
            total: users.length,
            active: users.filter(u => u.is_active).length,
            admins: users.filter(u => u.role === 'admin' || u.role === 'super_admin').length
          }
        }))
      }

      // Process cloudinary data
      if (cloudinaryData.status === 'fulfilled' && cloudinaryData.value.success) {
        const cloudinary = cloudinaryData.value.data
        setStats(prev => ({
          ...prev,
          cloudinary: {
            usage: cloudinary.usage || 0,
            limit: cloudinary.limit || 100,
            images: cloudinary.total_images || 0
          }
        }))
      }

      // Process system data
      if (systemData.status === 'fulfilled' && systemData.value.success) {
        const system = systemData.value.data
        setStats(prev => ({
          ...prev,
          system: {
            status: system.status || 'unknown',
            uptime: system.uptime || 0,
            memory: system.memory_usage || 0
          }
        }))
      }

      // Process debug data
      if (debugData.status === 'fulfilled' && debugData.value.success) {
        const debug = debugData.value.data
        setStats(prev => ({
          ...prev,
          debug: {
            errors: debug.error_count || 0,
            warnings: debug.warning_count || 0,
            performance: debug.avg_response_time || 0
          }
        }))
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, subtitle, icon, color = 'primary', progress }) => (
    <Card className="hover-card">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: `${color}.main`, mr: 2 }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={progress > 80 ? 'error' : progress > 60 ? 'warning' : 'success'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {progress.toFixed(1)}% used
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Super Admin Dashboard
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Chip 
          icon={<div className={`status-indicator ${stats.system.status === 'healthy' ? 'online' : 'offline'}`} />}
          label={`System Status: ${stats.system.status.toUpperCase()}`}
          color={stats.system.status === 'healthy' ? 'success' : 'error'}
          variant="outlined"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Users Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.users.total}
            subtitle={`${stats.users.active} active, ${stats.users.admins} admins`}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>

        {/* Cloudinary Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cloudinary Usage"
            value={`${(stats.cloudinary.usage / 1024 / 1024).toFixed(1)} MB`}
            subtitle={`${stats.cloudinary.images} images stored`}
            icon={<CloudIcon />}
            color="info"
            progress={(stats.cloudinary.usage / stats.cloudinary.limit) * 100}
          />
        </Grid>

        {/* System Performance */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Memory Usage"
            value={`${stats.system.memory.toFixed(1)}%`}
            subtitle={`Uptime: ${Math.floor(stats.system.uptime / 3600)}h`}
            icon={<SpeedIcon />}
            color="warning"
            progress={stats.system.memory}
          />
        </Grid>

        {/* Debug Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="System Errors"
            value={stats.debug.errors}
            subtitle={`${stats.debug.warnings} warnings, ${stats.debug.performance}ms avg`}
            icon={<BugReportIcon />}
            color={stats.debug.errors > 0 ? 'error' : 'success'}
          />
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="subtitle1">Manage Users</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add, edit, or remove users
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}>
                    <CloudIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="subtitle1">Cloudinary</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage image storage
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}>
                    <BugReportIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <Typography variant="subtitle1">Debug Console</Typography>
                    <Typography variant="body2" color="text.secondary">
                      View system logs
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}>
                    <SecurityIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="subtitle1">System Health</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monitor system status
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard